import client from './client';
import { PROCESSES_API } from './routes';

// ── Enums (mirror section 13.2 of the Processes plan) ────────────────────────

export type ProcessStatus = 'draft' | 'published';
/** Coarse classification of a node, mirrors the backend's
 *  {@code NodeKind} enum. The editor keys React Flow's node-type registry
 *  off this discriminator. */
export type NodeKind =
  | 'input'
  | 'task'
  | 'gate'
  | 'output'
  | 'note'
  | 'image_gen'
  | '3d_gen'
  | 'manual';
export type ArtifactKind =
  | 'NONE'
  | 'MODEL_3D'
  | 'IMAGE'
  | 'POINT_CLOUD'
  | 'DOCUMENT'
  | 'GCODE'
  | 'TEXT'
  | 'NUMBER';
export type PortDirection = 'input' | 'output';
/**
 * Wire-format Gate mode. Values match the backend
 * {@code com.aibuild.os.controlplane.process.definition.gate.GateMode}
 * enum names so the FE round-trips the same string the
 * {@code SELECT} field's {@code ConfigFieldOption} stores.
 *
 * - {@code 'MANUAL'} — human reviewer approves / rejects.
 * - {@code 'AUTO'} — configured cloud Task agent decides inline.
 */
export type GateMode = 'MANUAL' | 'AUTO';
/** Who approves a MANUAL gate: named individuals or a single group (AIB-7293). */
export type GateApproverType = 'USER' | 'GROUP';
/** Where a Task node's agent runs: pooled cloud VM or a user-owned local runner. */
export type RunnerTarget = 'CLOUD' | 'LOCAL';
/** Who a LOCAL Task node is assigned to: named individuals or a single group. */
export type AssigneeType = 'USER' | 'GROUP';
/** @deprecated Replaced by per-user {@code reviewerIds}. Kept for legacy wire compat. */
/** @deprecated No longer sent by the backend — retained only for wire compat with cached responses. */
export type GateReviewerRole = 'admin_only' | 'all_members';
export type NodeExecutionStatus = 'idle' | 'running' | 'completed' | 'failed';

// ── Core DTOs (gallery scope) ────────────────────────────────────────────────

export interface UserRefDto {
  readonly userId: string;
  readonly name: string;
  readonly email: string | null;
}

export interface ProcessListItemDto {
  readonly processId: string;
  readonly name: string;
  readonly description: string | null;
  readonly status: ProcessStatus;
  readonly nodeCount: number;
  readonly createdBy: UserRefDto;
  readonly isOwner: boolean;
  readonly isEditable: boolean;
  readonly publishedAt: string | null;
  readonly duplicatedFrom: string | null;
  readonly runCount: number;
  readonly avgDurationMs: number | null;
  readonly lastRunAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ProcessListResponse {
  readonly processes: readonly ProcessListItemDto[];
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

export interface TemplateDto {
  readonly templateId: string;
  readonly name: string;
  readonly description: string;
  readonly category: 'blank' | 'creative' | 'engineering';
  readonly previewImageUrl: string | null;
}

// ── Graph DTOs (mirror plan §13.2) ───────────────────────────────────────────
// Ports are first-class entities with stable UUIDs. Edges reference port
// UUIDs, not direction strings — renaming a port label is non-breaking, and
// the editor can carry per-port metadata (label, type, position) without
// inventing magic strings.

/**
 * A single port on a node. `portId` is a UUID, stable across renames.
 * `position` orders ports of the same direction within a node (0-based).
 *
 * `acceptedKinds` is the backend-derived set the port accepts (catalog +
 * config projection). Optional on the wire because legacy rows / older
 * servers omit it; consumers default to `[portType]` via
 * `acceptedKindsOf`. The write-side DTO intentionally drops the field —
 * the backend re-derives it on every save.
 */
export interface ProcessPortDto {
  readonly portId: string;
  readonly direction: PortDirection;
  readonly label: string;
  readonly portType: ArtifactKind;
  readonly acceptedKinds?: readonly ArtifactKind[];
  readonly position: number;
}

/**
 * Per-kind config. Every field is optional so a single shape covers
 * Input / Task / Gate / Output / Note. The renderer narrows by `kind`
 * (open-closed: adding a new kind = adding more optional fields, no
 * breaking changes for existing consumers).
 */
export interface ProcessNodeConfigDto {
  // Input / Output
  readonly dataType?: ArtifactKind;
  readonly required?: boolean;
  // Gate
  readonly mode?: GateMode;
  /** @deprecated Legacy field — use {@code reviewerIds} instead. */
  readonly reviewerRole?: 'admin_only' | 'all_members';
  readonly reviewerIds?: readonly string[];
  /** MANUAL approver mode. Defaults to USER server-side when absent. */
  readonly approverType?: GateApproverType;
  /** Selected org group id when {@link approverType} is GROUP. */
  readonly reviewerGroupId?: string;
  readonly reviewInstructions?: string;
  readonly timeoutHours?: number;
  readonly inputType?: ArtifactKind;
  /**
   * AUTO-mode reviewer skill id. Required when {@link mode} is
   * {@code 'AUTO'}; ignored for {@code 'MANUAL'}. Matches one of the
   * cloud Task skill ids exposed by the backend's
   * {@code TaskSkillCatalog} via the gate's {@code reviewerSkillId}
   * select field.
   */
  readonly reviewerSkillId?: string;
  /** Legacy key for {@link reviewerSkillId} — read-only compat for rows
   *  saved before the skillId rename. Never written on save. */
  readonly reviewerAgentId?: string;
  /**
   * AUTO-mode max iterations cap (1..10, default 5). Caps how many
   * times an AUTO gate may fire in a single run before the gate node
   * itself fails with "Maximum iterations reached without approval".
   * Backend mirror: {@code GateConfig#maxIterations} — a primitive
   * {@code int} that the {@code @JsonCreator} folds missing/null into
   * the default and clamps into 1..10, so the value coming OUT of the
   * API is always a number. The SELECT control on the gate detail
   * panel emits a string ({@code "5"}) when the user changes it;
   * Jackson coerces it back to the integer on save. Optional on the
   * union because the field is config-bag-shared with other node
   * kinds that don't carry it.
   */
  readonly maxIterations?: number | string;
  // Task (generic)
  readonly skillIds?: readonly string[];
  /** The first of {@link skillIds}, written alongside it so a control plane on
   *  the previous build still reads a skill off a node saved by this one. */
  readonly skillId?: string;
  /** Legacy key for {@link skillId} — read-only compat for rows saved
   *  before the skillId rename. Never written on save. */
  readonly agentId?: string;
  /**
   * Claude model (OpenRouter slug) for the agent's ReAct loop. Backend
   * mirror: {@code GenericTaskConfig#llmModel} — the canonical
   * constructor folds non-catalogued values back to null (agent default).
   */
  readonly llmModel?: string;
  readonly outputInstructions?: Record<string, string>;
  /**
   * Max credits the agent may spend on one execution of this node.
   * Positive number; absent = uncapped (the default). Backend mirror:
   * {@code GenericTaskConfig#maxCredits} — the canonical constructor
   * folds non-positive values back to uncapped.
   */
  readonly maxCredits?: number | null;
  /**
   * Where the Task agent runs. Absent / {@code 'CLOUD'} = the pooled
   * process VM (the historical default). {@code 'LOCAL'} runs on a
   * user-owned runner selected via the assignee fields below, which
   * mirror the gate approver model. Backend mirror:
   * {@code GenericTaskConfig#runnerTarget}.
   */
  readonly runnerTarget?: RunnerTarget;
  /** LOCAL assignee mode; ignored for CLOUD. Defaults to USER server-side. */
  readonly assigneeType?: AssigneeType;
  /** Named assignees when {@link assigneeType} is USER. */
  readonly assigneeIds?: readonly string[];
  /** Selected org group id when {@link assigneeType} is GROUP. */
  readonly assigneeGroupId?: string;
  // Manual — free-text instructions shown to the assignee.
  readonly instructions?: string;
  // Note
  readonly text?: string;
  // Image gen — persisted UI state
  readonly heroSize?: { readonly width: number; readonly height: number };
  readonly heroMinSize?: { readonly width: number; readonly height: number };
  /** Image Generation — selected fal.ai endpoints (blank = service default). */
  readonly genModel?: string;
  readonly editModel?: string;
}

/** Latest per-node test-run execution snapshot — null until run for the first time. */
export interface NodeExecutionDto {
  readonly status: NodeExecutionStatus;
  readonly executionTimeMs: number | null;
  /** Credits billed for this attempt; null/absent when nothing was billed. */
  readonly creditsSpent?: number | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly errorMessage: string | null;
  /** Raw executor text behind {@link errorMessage}, shown behind the dock's
   *  "Technical details" disclosure. Null when there is nothing to reveal. */
  readonly errorDetail?: string | null;
}

/** Process artifact — placeholder for now (full shape lands in the runs PR). */
export interface ProcessArtifactDto {
  readonly artifactId: string;
  readonly processId: string;
  readonly runId: string | null;
  readonly nodeId: string;
  readonly portId: string;
  readonly iteration: number;
  readonly name: string;
  readonly contentType: string;
  readonly fileSize: number;
  readonly downloadUrl: string;
  /**
   * Presigned URL for an internal, browser-renderable preview derived from a
   * CAD output such as STEP/BREP. Null/absent when the artifact is directly
   * renderable or has no preview.
   */
  readonly previewUrl?: string | null;
  /**
   * Filename of the preview asset. Its extension picks the loader: an STL when
   * the source had faces to tessellate, a GLB when it held only curves.
   */
  readonly previewName?: string | null;
  /**
   * Size in bytes of the preview asset, not the source CAD file. Inline
   * rendering is gated on this so a large preview can't slip past the size
   * limit. Null/absent when there is no preview.
   */
  readonly previewSizeBytes?: number | null;
  readonly createdAt: string;
}

/**
 * Node descriptor on a process detail / graph snapshot response.
 *
 * `definitionId` + `definitionVersion` pin the catalog row; `kind` is the
 * resolved definition's coarse classification (the editor keys React Flow's
 * node-type registry off it). Every other piece of editor-time shape (label,
 * icon, config schema, branches, artifacts panel style) lives on
 * `NodeDefinitionDto` from `useNodeDefinitions()` — this wire row stays
 * intentionally thin.
 */
export interface ProcessNodeDto {
  readonly nodeId: string;
  readonly definitionId: string;
  readonly definitionVersion: number;
  readonly kind: NodeKind;
  readonly label: string;
  readonly instruction: string | null;
  readonly config: ProcessNodeConfigDto;
  readonly ports: readonly ProcessPortDto[];
  readonly positionX: number;
  readonly positionY: number;
  readonly latestExecution: NodeExecutionDto | null;
  readonly latestArtifacts: readonly ProcessArtifactDto[];
}

export interface ProcessEdgeDto {
  readonly edgeId: string;
  readonly sourceNodeId: string;
  readonly sourcePortId: string;
  readonly targetNodeId: string;
  readonly targetPortId: string;
}

/**
 * Convenience slice of the graph — used by the editor's local state. The
 * wire endpoint (`GET /processes/{id}`) returns these inline on
 * `ProcessDetailDto`; this type exists for editor code that doesn't need
 * the surrounding process metadata (e.g. mock fixtures, useProcessGraph).
 */
export interface ProcessGraphDto {
  readonly nodes: readonly ProcessNodeDto[];
  readonly edges: readonly ProcessEdgeDto[];
}

/**
 * One issue produced by the backend's {@code ProcessValidator} chain. The
 * frontend renders these verbatim — `message` is the human-readable string,
 * and `code` is a stable enum-like discriminator the open-closed icon
 * registry switches on (unknown codes fall back to a generic icon, so a new
 * backend rule lights up in the UI without any frontend change).
 *
 * Any of `nodeId` / `edgeId` / `portId` may be `null` when the error is
 * graph-level rather than anchored to a specific entity.
 */
export type ValidationSeverity = 'ERROR' | 'WARNING';

export interface ValidationErrorDto {
  readonly rule: string;
  readonly code: string;
  readonly message: string;
  readonly nodeId: string | null;
  readonly edgeId: string | null;
  readonly portId: string | null;
  /** Absent on legacy responses; treated as `ERROR` by consumers. */
  readonly severity?: ValidationSeverity;
}

/**
 * Combined validation result. `valid` is `true` iff `errors` is empty — it
 * means "runnable as a draft", not "publishable". `warnings` carries
 * advisory findings that never block publish/run (e.g. a local node whose
 * assignees have no capable runner yet). `publishBlockers` carries findings
 * from PUBLISH_ONLY rules (missing Input/Output node, unwired port): they
 * block Publish but never a draft run.
 */
export interface ValidationResultDto {
  readonly valid: boolean;
  readonly errors: readonly ValidationErrorDto[];
  readonly warnings?: readonly ValidationErrorDto[];
  readonly publishBlockers?: readonly ValidationErrorDto[];
}

/**
 * Returned by `GET /processes/:id` — the process metadata plus the full
 * graph (nodes + edges) and the latest backend-computed `validation`
 * result. Mirrors the plan's `ProcessDetailDto` exactly. The list endpoint
 * never includes `nodes`/`edges`/`validation` so the gallery query stays
 * cheap.
 *
 * `validation` is the SINGLE SOURCE OF TRUTH for graph-shape correctness
 * — the FE no longer runs its own graph validator; it reads this field
 * (which is refreshed atomically on every save / fetch) and prunes errors
 * referencing entities the user has just deleted locally before render.
 */
export interface ProcessDetailDto {
  readonly processId: string;
  readonly name: string;
  readonly description: string | null;
  readonly status: ProcessStatus;
  readonly maxIterationsPerNode: number;
  readonly publishedAt: string | null;
  readonly duplicatedFrom: string | null;
  readonly createdBy: UserRefDto;
  readonly isOwner: boolean;
  readonly isEditable: boolean;
  readonly hasActiveTestRun: boolean;
  readonly nodes: readonly ProcessNodeDto[];
  readonly edges: readonly ProcessEdgeDto[];
  readonly validation: ValidationResultDto;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ── Write-side DTOs (mirror plan §13.2 -- Update*Request body) ──────────────
// These narrow ProcessNodeDto / ProcessPortDto / ProcessEdgeDto to ONLY the
// fields the server accepts on save. We deliberately drop:
//   • Catalog-derived projections (`kind` -- the server reads it off the
//     resolved definition).
//   • Per-run/per-test snapshot data (`latestExecution`, `latestArtifacts`).
//   • Resolved user refs (createdBy etc.) -- save bodies don't carry them.
//
// Keeping the write shape distinct from the read shape stops accidental
// drift: every field on the read DTO doesn't have to be a save-able field
// (and vice versa).

/**
 * Port payload sent on auto-save. Intentionally omits {@code acceptedKinds}:
 * the backend is the single source of truth for both {@code portType} (on
 * bound ports — Input/Output {@code dataType}, Gate {@code inputType}) and
 * the full multi-type accepted set (e.g. image-gen Prompt's {TEXT, IMAGE}).
 * On every save the BE projects from the catalog + the wire {@code config}
 * and rewrites the persisted port_type / accepted_kinds_json columns. The
 * FE round-trip just sends the user's intent (config + structural port
 * shape) and renders whatever comes back.
 */
export interface WriteProcessPortDto {
  portId: string;
  direction: PortDirection;
  label: string;
  portType: ArtifactKind;
  position: number;
}

export interface WriteProcessNodeDto {
  nodeId: string;
  definitionId: string;
  definitionVersion: number;
  label: string;
  instruction: string | null;
  config: ProcessNodeConfigDto;
  ports: readonly WriteProcessPortDto[];
  positionX: number;
  positionY: number;
}

export interface WriteProcessEdgeDto {
  edgeId: string;
  sourceNodeId: string;
  sourcePortId: string;
  targetNodeId: string;
  targetPortId: string;
}

/**
 * The body the auto-save / explicit save sends to `PUT /processes/:id`.
 * Mirrors the backend's `UpdateProcessRequest` exactly.
 *
 * `expectedUpdatedAt` is the optimistic-lock token: the value of
 * `updatedAt` the FE last observed. The server compares it against the
 * row's current `updated_at` and returns `409 DRIFT_DETECTED` (with the
 * fresh `currentUpdatedAt` in `details`) if they don't match — so we
 * never silently overwrite a teammate's edits.
 */
export interface UpdateProcessRequest {
  name: string;
  description: string | null;
  maxIterationsPerNode: number;
  expectedUpdatedAt: string;
  nodes: readonly WriteProcessNodeDto[];
  edges: readonly WriteProcessEdgeDto[];
}

/**
 * Body for the stateless `POST /processes/:id/validate` endpoint —
 * just the in-flight graph. The editor calls this on every (debounced)
 * graph change so per-node error overlays stay fresh without waiting
 * for the autosave round-trip.
 */
export interface ValidateProcessRequest {
  nodes: readonly WriteProcessNodeDto[];
  edges: readonly WriteProcessEdgeDto[];
}

// ── Filters ──────────────────────────────────────────────────────────────────

export type ProcessListStatusFilter = 'all' | ProcessStatus;

/**
 * Sort modes for the gallery list. `newest` / `oldest` order by `updatedAt`,
 * `name` is a case-insensitive A→Z. The server is the source of truth — the
 * client sorts nothing locally so pagination stays stable.
 */
export type ProcessListSort = 'newest' | 'oldest' | 'name';

export interface ListProcessesParams {
  status?: ProcessListStatusFilter;
  search?: string;
  sort?: ProcessListSort;
  page?: number;
  size?: number;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

const BASE = PROCESSES_API;

export const runArtifactsBundleUrl = (
  processId: string,
  runId: string,
  nodeId?: string,
): string => {
  const path = `${BASE}/${encodeURIComponent(processId)}/runs/${encodeURIComponent(runId)}/artifacts/download-all`;
  return nodeId ? `${path}?nodeId=${encodeURIComponent(nodeId)}` : path;
};

export async function getProcess(processId: string): Promise<ProcessDetailDto> {
  const { data } = await client.get<ProcessDetailDto>(
    `${BASE}/${encodeURIComponent(processId)}`,
  );
  return data;
}

export async function listProcesses(
  params: ListProcessesParams = {},
): Promise<ProcessListResponse> {
  const { status = 'all', search, sort = 'newest', page = 0, size = 20 } = params;
  const { data } = await client.get<ProcessListResponse>(BASE, {
    params: {
      status,
      ...(search ? { search } : {}),
      sort,
      page,
      size,
    },
  });
  return data;
}

export interface CreateProcessRequest {
  name: string;
  description?: string;
  templateId?: string;
}

export async function createProcess(
  body: CreateProcessRequest,
): Promise<ProcessDetailDto> {
  const { data } = await client.post<ProcessDetailDto>(BASE, body);
  return data;
}

export async function deleteProcess(processId: string): Promise<void> {
  await client.delete(`${BASE}/${encodeURIComponent(processId)}`);
}

/**
 * Lightweight partial-update for the gallery — only fields that are safe to
 * change without re-sending the entire graph (which is what the heavyweight
 * `PUT /processes/:id` requires). Server merges the fields that are present;
 * absent fields are left unchanged. Same ownership + status guards as the
 * spec's PUT (only the owner can patch a draft).
 */
export interface PatchProcessRequest {
  name?: string;
  description?: string | null;
}

export async function patchProcess(
  processId: string,
  body: PatchProcessRequest,
): Promise<ProcessDetailDto> {
  const { data } = await client.patch<ProcessDetailDto>(
    `${BASE}/${encodeURIComponent(processId)}`,
    body,
  );
  return data;
}

/**
 * Full graph save — used by the editor's auto-save loop. The body is the
 * complete graph (full-replace semantics on the server) plus the optimistic
 * lock token. On 409 the response body's `details.currentUpdatedAt` carries
 * the fresh server-side timestamp so the caller can present a Refresh
 * affordance instead of silently dropping the user's edits.
 *
 * Returns the fresh `ProcessDetailDto` (incl. validation), so the caller
 * can update its `expectedUpdatedAt` token and chip without a follow-up
 * GET.
 */
export async function updateProcess(
  processId: string,
  body: UpdateProcessRequest,
): Promise<ProcessDetailDto> {
  const { data } = await client.put<ProcessDetailDto>(
    `${BASE}/${encodeURIComponent(processId)}`,
    body,
  );
  return data;
}

/**
 * Publish round-trip body. The optional {@code expectedUpdatedAt} is the
 * same drift token shape the editor's auto-save sends to PUT — it pins the
 * publish to a specific committed graph version so a stale tab can't
 * silently freeze a graph the user never reviewed.
 *
 * Backend treats absent / blank values as "no drift check" (back-compat
 * with the original parameterless contract); the FE always sends one when
 * the detail cache is populated.
 */
export interface PublishProcessRequest {
  expectedUpdatedAt?: string;
}

/**
 * Validates the draft and transitions it to PUBLISHED. Server-side guards:
 * owner-only, no active test run, validation must pass. Returns the fresh
 * detail (now with `status === 'published'`, `publishedAt` set) so the
 * caller can update the cache without a follow-up GET.
 *
 * Error codes the FE branches on:
 *   - 422 VALIDATION_FAILED: shouldn't happen if the caller already ran
 *     `getValidation` first, but treat as "fix errors and retry".
 *   - 409 DRIFT_DETECTED: same shape as the auto-save's drift; the
 *     response body's `details.currentUpdatedAt` carries the fresh server
 *     timestamp. Caller refetches the detail.
 *   - 409 TEST_RUN_ACTIVE: cancel the active test run first.
 */
export async function publishProcess(
  processId: string,
  body: PublishProcessRequest = {},
): Promise<ProcessDetailDto> {
  const { data } = await client.post<ProcessDetailDto>(
    `${BASE}/${encodeURIComponent(processId)}/publish`,
    body,
  );
  return data;
}

export interface DuplicateProcessRequest {
  newName?: string;
}

export async function duplicateProcess(
  processId: string,
  body: DuplicateProcessRequest = {},
): Promise<ProcessDetailDto> {
  const { data } = await client.post<ProcessDetailDto>(
    `${BASE}/${encodeURIComponent(processId)}/duplicate`,
    body,
  );
  return data;
}

export interface SendCopyRequest {
  targetUserId: string;
  newName?: string;
}

export interface SendCopyResponse {
  processId: string;
  name: string;
  targetUser: UserRefDto;
}

export async function sendProcessCopy(
  processId: string,
  body: SendCopyRequest,
): Promise<SendCopyResponse> {
  const { data } = await client.post<SendCopyResponse>(
    `${BASE}/${encodeURIComponent(processId)}/send-copy`,
    body,
  );
  return data;
}

export async function listTemplates(): Promise<readonly TemplateDto[]> {
  const { data } = await client.get<readonly TemplateDto[]>(`${BASE}/templates`);
  return data;
}

/**
 * Per-status totals for the gallery's segmented-control badges. Returned by
 * `GET /api/v1/processes/counts` in a single round-trip; replaces the
 * previous three `?size=1` list calls.
 *
 * Visibility filter mirrors {@link listProcesses}: own drafts plus every
 * published process in the same tenant. The optional `search` filter is
 * passed through verbatim so badge counts stay consistent with the
 * filtered list view.
 *
 * `all === draft + published` by construction; both fields are kept on the
 * wire so the FE doesn't have to re-add them.
 */
export interface ProcessCountsDto {
  readonly all: number;
  readonly draft: number;
  readonly published: number;
}

export async function getProcessesCounts(
  search?: string,
): Promise<ProcessCountsDto> {
  const { data } = await client.get<ProcessCountsDto>(`${BASE}/counts`, {
    params: search ? { search } : {},
  });
  return data;
}

/**
 * Standalone validation endpoint — `GET /processes/:id/validate`. Used by
 * the Publish click as an explicit "fresh check now" before the mutation,
 * so the user can't race a stale optimistic cache. Most callers should
 * just read `detailQuery.data.validation` instead — every endpoint that
 * returns a `ProcessDetailDto` already carries a fresh validation result.
 */
export async function getValidation(
  processId: string,
): Promise<ValidationResultDto> {
  const { data } = await client.get<ValidationResultDto>(
    `${BASE}/${encodeURIComponent(processId)}/validate`,
  );
  return data;
}

/**
 * Stateless validation of an in-flight graph payload. Doesn't touch
 * the DB and doesn't bump `updatedAt` — safe to fire on every
 * (debounced) graph edit so the editor's per-node overlays stay fresh
 * without waiting for the autosave round-trip.
 *
 * Accepts an {@link AbortSignal} so the caller can cancel a request
 * the user has already invalidated with a newer edit.
 */
export async function validateProcessPending(
  processId: string,
  body: ValidateProcessRequest,
  signal?: AbortSignal,
): Promise<ValidationResultDto> {
  const { data } = await client.post<ValidationResultDto>(
    `${BASE}/${encodeURIComponent(processId)}/validate`,
    body,
    { signal },
  );
  return data;
}

/**
 * Reset every node's test-run execution rows + remove all run-bound
 * artifacts for the process — the wipe behind the topbar's "Clear
 * statuses" button. Triggers no SSE events; the React Query cache for
 * the detail must be invalidated by the caller.
 */
export async function clearProcessStatuses(processId: string): Promise<void> {
  await client.post<void>(
    `${BASE}/${encodeURIComponent(processId)}/clear-statuses`,
  );
}

// ── Image model catalog ──────────────────────────────────────────────────────
// Curated fal.ai image models for the Image Generation node's model dropdown.
// `mode=generate` lists text-to-image models, `mode=edit` lists image-to-image
// models — the editor switches based on whether an image is wired to the node.

export type ImageModelMode = 'generate' | 'edit';

export interface ImageModelDto {
  readonly id: string;
  readonly displayName: string;
  readonly description: string;
  /** Credits charged per generated image (1 credit = $1). */
  readonly creditCost: number;
  /** Credits added per reference image an edit sends; 0 for flat-priced models. */
  readonly referenceCreditCost?: number;
}

export async function listImageModels(
  mode: ImageModelMode,
): Promise<readonly ImageModelDto[]> {
  const { data } = await client.get<readonly ImageModelDto[]>(
    '/api/v1/image-models',
    { params: { mode } },
  );
  return data;
}
